import React, { useState, useEffect } from 'react';
import { HiOutlineQuestionMarkCircle, HiOutlineChat, HiOutlineCalendar, HiOutlineUser } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../config/axios';
import toastService from '../../../services/toast';

const CourseCard = ({ course }) => {
  const navigate = useNavigate();
  
  // تنسيق التاريخ للعربية
  const formatLastActivity = (dateString) => {
    if (!dateString) return 'لا يوجد نشاط';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'اليوم';
    } else if (diffDays === 1) {
      return 'الأمس';
    } else if (diffDays < 7) {
      return `قبل ${diffDays} أيام`;
    } else {
      return date.toLocaleDateString('ar-EG');
    }
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">{course.title || course.name || 'دورة بدون اسم'}</h2>
          <span className={`px-3 py-1 rounded-full text-sm ${
            course.questionsCount > 0 
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
          }`}>
            {course.questionsCount || 0} سؤال
          </span>
        </div>
      </div>
      
      <div className="px-6 py-4">
        <div className="mb-4 grid grid-cols-2 gap-4">
          <div className="flex items-center text-gray-600 dark:text-gray-300">
            <HiOutlineCalendar className="h-5 w-5 ml-2" />
            <span>آخر نشاط: {formatLastActivity(course.lastActivity)}</span>
          </div>
          <div className="flex items-center text-gray-600 dark:text-gray-300">
            <HiOutlineUser className="h-5 w-5 ml-2" />
            <span>{course.traineesCount || 0} متدرب نشط</span>
          </div>
        </div>
        
        <div className="flex justify-end">
          <button 
            onClick={() => navigate(`/admin/dashboard/ask-instructor/chat/${course.id}`)}
            className="px-4 py-2 rounded-lg flex items-center bg-blue-600 hover:bg-blue-700 text-white transition-colors duration-200"
          >
            <HiOutlineChat className="h-5 w-5 ml-2" />
            عرض المحادثات
          </button>
        </div>
      </div>
    </div>
  );
};

const AskInstructor = () => {
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // جلب الدورات التي يدرسها المدرب وممكن فيها تواصل مع المتدربين
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // استدعاء API لجلب الدورات التي يدرسها المدرب ومفعل بها خاصية اسأل مدرب
        const response = await axiosInstance.get('/api/admin/instructor/courses-with-ask-feature');
        
        if (response.data && Array.isArray(response.data)) {
          setCourses(response.data);
        } else {
          // تنسيق بيانات غير صالح من API
          setCourses([]);
        }
      } catch (error) {
        // خطأ في جلب الدورات
        setError('حدث خطأ في تحميل الدورات. يرجى المحاولة مرة أخرى.');
        toastService.error('حدث خطأ في تحميل الدورات');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCourses();
  }, []);

  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-t-lg rounded-b-lg">
        {/* رأس البطاقة الرئيسية */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h1 className="text-lg font-medium text-gray-900 dark:text-white">اسأل مدرب</h1>
          <HiOutlineQuestionMarkCircle className="h-6 w-6 text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400 transition-colors duration-200" />
        </div>

        {/* محتوى البطاقة */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : error ? (
            <div className="text-center text-red-500 dark:text-red-400 py-8">
              {error}
              <button 
                onClick={() => window.location.reload()} 
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                إعادة المحاولة
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {courses.map(course => (
                  <CourseCard key={course.id} course={course} />
                ))}
              </div>
              
              {courses.length === 0 && (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  لا توجد دورات مفعل فيها خاصية اسأل مدرب
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AskInstructor;
